// The console's connection to Estra Cloud's control plane (deploy/cloud): sign-in, the projects and
// accounts collections through Estra's own client, callables, and the JSON routes of
// docs/console-api.md (stats, the console proxy to the project's backend, mail and push).
import { httpTransport } from "@estra/client";
import { browserStorage, createAuth } from "@estra/client/auth";
import { getFirestore } from "@estra/client/firestore";
import { getFunctions, httpsCallable } from "@estra/client/functions";

const LOCAL = location.hostname === "localhost" || location.hostname === "127.0.0.1";
export const API = new URLSearchParams(location.search).get("api") ?? (LOCAL ? "http://127.0.0.1:8796" : "https://api.estra.cloud");

export const auth = createAuth({ url: API, storage: browserStorage() });
export const db = getFirestore(httpTransport({ url: API, getToken: () => auth.getIdToken() }));
export const fns = getFunctions({ url: API, getToken: () => auth.getIdToken() });

export const call = <I, O>(name: string, input: I) => httpsCallable<I, O>(fns, name)(input).then((r) => r.data);

/** An error from a JSON route, with the route's code ("not-found", "failed-precondition", …) and HTTP status. */
export class ApiError extends Error {
  code: string;
  status: number;
  constructor(message: string, code: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

/** GET (no body) or POST (a body) to a control-plane route with the session. */
export async function api<T = any>(path: string, body?: unknown, method?: string): Promise<T> {
  const r = await fetch(`${API}${path}`, {
    method: method ?? (body === undefined ? "GET" : "POST"),
    headers: { authorization: `Bearer ${await auth.getIdToken()}`, "content-type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const out = (await r.json().catch(() => ({}))) as { message?: string; error?: string | { code?: string; message?: string } };
  if (!r.ok) {
    const e = typeof out.error === "object" ? out.error : null;
    throw new ApiError(out.message ?? e?.message ?? `HTTP ${r.status}`, (typeof out.error === "string" ? out.error : e?.code) ?? String(r.status), r.status);
  }
  return out as T;
}

const withProject = (path: string, id: string) => `${path}${path.includes("?") ? "&" : "?"}project=${encodeURIComponent(id)}`;
/** A project's mail (`/v1/mail/my/*`) or push (`/v1/push/my/*`); admins' own routes are `/v1/{mail,push}/admin/*`. */
export type ScopedApi = (path: string, body?: unknown) => Promise<any>;
export const myMail = (id: string): ScopedApi => (path, body) => api(withProject(`/v1/mail/my/${path}`, id), body);
export const myPush = (id: string): ScopedApi => (path, body) => api(withProject(`/v1/push/my/${path}`, id), body);
export const adminMail: ScopedApi = (path, body) => api(`/v1/mail/admin/${path}`, body);
export const adminPush: ScopedApi = (path, body) => api(`/v1/push/admin/${path}`, body);
/** The project's backend console routes, through the control plane (which adds the backend's key). */
export const tenant = (id: string) => <T = any>(path: string, body?: unknown) => api<T>(`/v1/console/${encodeURIComponent(id)}/${path}`, body);

export function errText(e: unknown): string {
  const code = (e as { code?: string }).code ?? "";
  if (code.includes("permission-denied")) return "That isn't allowed. Refresh and try again.";
  return (e as Error).message || "Something went wrong.";
}
/** True when a route isn't on the control plane yet (an older deploy): pages fall back quietly. */
export const missing = (e: unknown) => e instanceof ApiError && (e.status === 404 || e.status === 405) && !/project/i.test(e.message);
/**
 * True when the backend can't answer a console route yet: the route isn't there (an older backend or
 * control plane), the project has no backend (409), or nothing was pushed to it yet (412).
 */
export const unavailable = (e: unknown) => missing(e) || (e instanceof ApiError && (e.status === 409 || e.status === 412 || (e.status === 404 && e.code === "not-found" && /console/i.test(e.message))));
